import React, { useEffect, useState } from "react";
import ManagementSidebar from "./ManagementSidebar";
import {
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Pagination,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import SecurityOutlinedIcon from "@mui/icons-material/SecurityOutlined";

import axios from "axios";
import { showToast, WEBSERVICE } from "../../CoreDataService";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { ContainedButton } from "../../Common/Buttons";

// ── Design tokens ────────────────────────────────────────────────────────────
const pageWrapper = {
  px: { xs: 2, md: 4 },
  py: { xs: 2, md: 3 },
  backgroundColor: "var(--body-color)",
  color: "var(--text-color)",
  minHeight: "calc(100vh - 64px)",
};

const contentHero = {
  borderRadius: "18px",
  px: { xs: 3, md: 4 },
  py: { xs: 2.5, md: 3 },
  mb: 3,
  position: "relative",
  overflow: "hidden",
  boxShadow: "0 10px 30px rgba(0,0,0,0.18)",
};

const heroContent = { display: "flex", alignItems: "center", gap: 2 };

const heroIconBadge = {
  width: 52,
  height: 52,
  borderRadius: "14px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "rgba(255,255,255,0.18)",
  border: "1px solid rgba(255,255,255,0.25)",
  backdropFilter: "blur(6px)",
  flexShrink: 0,
};

const heroTitle = {
  fontSize: { xs: "1.2rem", md: "1.5rem" },
  fontWeight: 700,
  color: "#fff",
  lineHeight: 1.15,
};

const heroSubtitle = {
  fontSize: "0.85rem",
  color: "rgba(255,255,255,0.88)",
  mt: 0.4,
};

const filterCard = {
  backgroundColor: "var(--body-color-less2)",
  borderRadius: "12px",
  border: "1px solid rgba(125,125,125,0.12)",
  p: { xs: 2, md: 2.5 },
  mb: 3,
};

const mainCard = {
  backgroundColor: "var(--body-color-less)",
  borderRadius: "16px",
  border: "1px solid rgba(125,125,125,0.15)",
  boxShadow: "0 1px 3px rgba(0,0,0,0.06), 0 10px 25px rgba(0,0,0,0.06)",
  overflow: "hidden",
};

const modernTableContainer = {
  borderRadius: "0",
  border: "none",
  boxShadow: "none",
  overflowX: "auto",
  backgroundColor: "var(--body-color-less)",
};

const modernTableHeadRow = { backgroundColor: "var(--body-color-less2)" };

const modernTableHeadCell = {
  fontWeight: 700,
  fontSize: "0.72rem",
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  color: "var(--text-color)",
  opacity: 0.75,
  borderBottom: "1px solid rgba(125,125,125,0.15)",
  py: 1.75,
  whiteSpace: "nowrap",
};

const modernTableBodyRow = {
  transition: "background-color 0.15s ease",
  "&:hover": { backgroundColor: "var(--body-color-less2)" },
  "&:last-child td": { borderBottom: 0 },
};

const modernTableBodyCell = {
  fontSize: "0.85rem",
  py: 1.65,
  borderBottom: "1px solid rgba(125,125,125,0.12)",
  color: "var(--text-color)",
  whiteSpace: "nowrap",
};

const modernFieldStyle = {
  "& .MuiInputBase-root": {
    borderRadius: "10px",
    backgroundColor: "var(--body-color)",
    color: "var(--text-color)",
  },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(125,125,125,0.25)" },
  "& .MuiInputLabel-root": { color: "var(--text-color)", opacity: 0.7 },
  "& .MuiSelect-icon": { color: "var(--text-color)" },
};
// ─────────────────────────────────────────────────────────────────────────────

const LoginHistory = () => {
  const location = useLocation();
  const { btncolor } = useSelector((state) => state);
  const openIndex = location.state?.openIndex;
  const [selectedSubAccount, setSelectedSubAccount] = useState("");
  const [subAccountsData, setSubAccountsData] = useState([]);

  const [selectedUuid, setSelectedUuid] = useState("");
  const [selectedEmail, setSelectedEmail] = useState("");
  const [historyList, setHistoryList] = useState([]);
  // These state values are written by the API response handler; kept for parity with original logic
  const [, setTotalCount] = useState(0);
  const [, setPgn] = useState([]);
  const [, setPgnLength] = useState(0);

  const [totalItem, setTotalItem] = useState(0);
  const [pageNo, setPageNo] = useState(1);
  const itemsPerPage = 10;
  const totalPages = Math.ceil(totalItem / itemsPerPage);

  useEffect(() => {
    getListOfAllSubAccounts();
  }, []);

  const getListOfAllSubAccounts = async () => {
    const payload = {
      parentId: localStorage.getItem("uuid"),
    };

    try {
      const response = await axios.post(
        `${WEBSERVICE}assetManagement/getAllSubAccounts`,
        JSON.stringify(payload),
        {
          headers: {
            "Content-Type": "application/json",
            authorization: "BEARER " + localStorage.getItem("access_token"),
          },
        }
      );

      const res = response.data;

      if (res.statusCode !== 0) {
        showToast("error", res.message);
      } else {
        setSubAccountsData(res["sub accounts"]);
      }
    } catch (error) {
      showToast("error", "Something went wrong!");
    }
  };

  const handlePageChange = (_event, pgno) => {
    setPageNo(pgno);
    loginHistoryList(pgno);
  };

  const handleChange = (event) => {
    const selectedValue = event.target.value;
    setSelectedSubAccount(selectedValue);
    setSelectedUuid(selectedValue?.uuid);
    setSelectedEmail(selectedValue?.email);
  };

  const loginHistoryList = (pageNo) => {
    const payload = {
      parentId: localStorage.getItem("uuid"),
      uuid: selectedUuid,
      noOfItemsPerPage: 5,
      pageNo: pageNo,
    };

    const jsonString = JSON.stringify(payload);

    axios
      .post(`${WEBSERVICE}assetManagement/subAccountLoginHistory`, jsonString, {
        headers: {
          "Content-Type": "application/json",
          authorization: "BEARER " + localStorage.getItem("access_token"),
        },
      })
      .then((response) => {
        console.log(response.data);
        const res = response.data.loginDetails;
        setTotalCount(response.data.totalRows);

        const updateArray = res.map((obj) => ({
          ...obj,
          email: selectedEmail,
        }));

        setHistoryList(updateArray);
        setTotalItem(updateArray?.length);
        console.log(updateArray);

        const pagination = [];
        for (let i = 1; i <= Math.ceil(response.data.totalRows / 20); i++) {
          pagination.push(i);
        }
        setPgn(pagination);
        setPgnLength(pagination.length);
      })
      .catch((error) => {
        console.error("Error fetching login history:", error);
      });
  };

  return (
    <Grid container spacing={1} sx={{ mx: 1 }}>
      <Grid size={{ xs: 12, md: 2 }}>
        <ManagementSidebar index={openIndex} />
      </Grid>
      <Grid size={{ xs: 12, md: 10 }}>
        <Box sx={pageWrapper}>
          {/* Hero Banner */}
          <Box
            sx={{
              ...contentHero,
              background: `linear-gradient(135deg, ${alpha(btncolor || "#1976d2", 0.95)} 0%, ${alpha(btncolor || "#1976d2", 0.75)} 100%)`,
            }}
          >
            <Box sx={heroContent}>
              <Box sx={heroIconBadge}>
                <SecurityOutlinedIcon sx={{ color: "#fff", fontSize: 26 }} />
              </Box>
              <Box>
                <Typography sx={heroTitle}>Login History</Typography>
                <Typography sx={heroSubtitle}>
                  Track access and security events for sub-accounts
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Filter Card */}
          <Box sx={filterCard}>
            <Grid container spacing={2} alignItems="center">
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <FormControl fullWidth sx={modernFieldStyle}>
                  <InputLabel>Select Sub Accounts</InputLabel>
                  <Select
                    value={selectedSubAccount}
                    label="Select Sub Accounts"
                    onChange={handleChange}
                  >
                    <MenuItem value="">
                      <em>Select Sub Accounts</em>
                    </MenuItem>
                    {subAccountsData?.map((account) => (
                      <MenuItem key={account.uuid} value={account}>
                        {account.email}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                <ContainedButton
                  variant="contained"
                  onClick={() => loginHistoryList(1)}
                >
                  Search
                </ContainedButton>
              </Grid>
            </Grid>
          </Box>

          {/* Table Card */}
          <Box sx={mainCard}>
            <TableContainer component={Box} sx={modernTableContainer}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={modernTableHeadRow}>
                    <TableCell sx={modernTableHeadCell}>Email</TableCell>
                    <TableCell sx={modernTableHeadCell}>Time</TableCell>
                    <TableCell sx={modernTableHeadCell}>IP Address</TableCell>
                    <TableCell sx={modernTableHeadCell}>Location</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {historyList?.length !== 0 ? (
                    historyList?.map((item, index) => (
                      <TableRow key={index} sx={modernTableBodyRow}>
                        <TableCell sx={modernTableBodyCell}>{item?.email}</TableCell>
                        <TableCell sx={modernTableBodyCell}>{item?.timestamp}</TableCell>
                        <TableCell sx={modernTableBodyCell}>{item?.ipAddress}</TableCell>
                        <TableCell sx={modernTableBodyCell}>{item?.location}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={4}
                        sx={{ ...modernTableBodyCell, textAlign: "center", py: 4, opacity: 0.55 }}
                      >
                        No Data
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            <Box sx={{ display: "flex", justifyContent: "flex-end", p: 2.5 }}>
              <Pagination
                count={totalPages}
                page={pageNo}
                boundaryCount={2}
                siblingCount={2}
                onChange={handlePageChange}
                shape="rounded"
                size="small"
                showFirstButton
                showLastButton
                sx={{
                  "& .MuiPaginationItem-root": {
                    color: "var(--text-color)",
                    borderColor: "rgba(125,125,125,0.25)",
                    "&.Mui-selected": {
                      backgroundColor: alpha(btncolor || "#1976d2", 0.85),
                      color: "#fff",
                      borderColor: "transparent",
                    },
                    "&:hover": {
                      backgroundColor: alpha(btncolor || "#1976d2", 0.15),
                    },
                  },
                }}
              />
            </Box>
          </Box>
        </Box>
      </Grid>
    </Grid>
  );
};

export default LoginHistory;
